import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { Subject, filter, map, merge } from 'rxjs';
import { FormField, createActionState, formValueMap } from '@at/shared';
import { InvitesApi } from '../../api/invites.api';
import { USER_CONTACTS_STATE, DisplayNameComponent } from '@at/user';
import { CdkListbox, CdkOption } from '@angular/cdk/listbox';

@Component({
  selector: 'at-invite',
  standalone: true,
  imports: [ReactiveFormsModule, FormField, CdkListbox, CdkOption, DisplayNameComponent],
  templateUrl: './invite.component.html',
  styleUrl: './invite.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InviteComponent {
  readonly #tripInvitesApi = inject(InvitesApi);
  readonly #contacts = inject(USER_CONTACTS_STATE);

  public dialog = input.required<HTMLDialogElement>();
  public tripId = input.required<string>();

  protected readonly sendInvite = new Subject<void>();
  protected readonly userContacts = this.#contacts;
  protected readonly form = new FormGroup({
    email: new FormControl(null, {
      validators: [Validators.email],
    }),
    emails: new FormControl(new Array<string>(), { nonNullable: true }),
  });
  protected readonly contactsFilter = signal<string>('');
  protected readonly contactsFiltered = computed(() => {
    const filter = this.contactsFilter();
    const contacts = this.userContacts().data?.contacts;

    if (!contacts) return [];
    if (!filter) return contacts;

    return contacts.filter((contact) => contact.email.includes(filter));
  });
  protected readonly userHasContacts = computed(() => {
    const contacts = this.userContacts().data?.contacts;
    return contacts && contacts.length > 0;
  });

  protected readonly selectedContacts = [];

  protected readonly state = toSignal(
    createActionState({
      trigger: this.sendInvite.pipe(
        formValueMap(this.form),
        map((formValue) => (formValue.email ? [...formValue.emails, formValue.email] : formValue.emails)),
        filter((emails) => {
          const result = emails.length > 0;

          if (!result) {
            this.form.setErrors({ required: true, message: 'At least one email is required' });
            this.form.markAllAsTouched();
          }

          return result;
        }),
      ),
      apiCall: (emails: string[]) => {
        return merge(...emails.map((email) => this.#tripInvitesApi.sendInvite({ tripId: this.tripId(), email })));
      },
      beforeStart: () => this.form.disable(),
      onSucceed: () => this.closeDialog('success'),
      onError: () => this.form.enable(),
    }),
  );

  protected closeDialog(status: 'success' | 'cancel' = 'cancel'): void {
    this.dialog().close(status);

    this.form.reset();
    this.form.enable();
  }
}
